import { range } from "lodash";

import { getRatingScaleClassName } from "@apps/ratings/helpers";
import Avatars from "@components/avatar/avatars";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { AssessmentAnswerWithResponderAndId } from "./questions/types";

const AssessmentQuestionRangeOptions = ({
  answer,
  answerHasChanged,
  otherParticipantsAnswers,
  startValue,
  endValue,
  disabled,
  labels,
  labelDescriptions,
  onClickAnswer,
}: {
  answer?: number | null;
  answerHasChanged?: boolean;
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  startValue: number;
  endValue: number;
  disabled?: boolean;
  labels: ReadonlyArray<string | null>;
  labelDescriptions: ReadonlyArray<string | null>;
  onClickAnswer?: (answer: number) => void;
}) => {
  const hasOtherAnswers =
    otherParticipantsAnswers && otherParticipantsAnswers.length > 0;
  return (
    <div
      className={classNames(
        `gap-2 grid`,
        getRatingScaleClassName(startValue, endValue),
      )}
    >
      {range(startValue, endValue + 1).map((value, index) => (
        <Tooltip
          key={value}
          text={
            labelDescriptions[index]
              ? `<div>${labelDescriptions[index]
                  ?.split("\n")
                  .join("<br/>")}</div>`
              : undefined
          }
        >
          <button
            contentEditable={false}
            className={classNames(
              "flex flex-col gap-1.5 items-center cursor-default rounded p-3",
              answer !== value && "border border-gray-100 bg-gray-50",
              answer === value && "bg-blue-100",
              !disabled && "hover:bg-gray-200 cursor-pointer",
              !disabled && answer === value && "hover:bg-blue-100",
            )}
            onClick={(evt) => {
              evt.preventDefault();
              if (onClickAnswer) {
                onClickAnswer(value);
              }
            }}
            disabled={disabled}
            aria-label="Range assessment quesiton option"
          >
            <div
              className={classNames(
                "text-sm font-semibold w-full text-center tracking-tighter leading-tight",
                answer !== value && "text-gray-500",
                answer === value && "text-gray-700",
              )}
            >
              {labels[index] || value}{" "}
            </div>
            <div className="flex w-full justify-center">
              {answer === value ? (
                <TopicflowIcon
                  type="radioButtonChecked"
                  className="h-4 w-4 text-blue-600"
                />
              ) : (
                <TopicflowIcon
                  type="radioButtonUnchecked"
                  className="h-4 w-4 text-gray-500"
                />
              )}
            </div>
            {hasOtherAnswers && (
              <div className="h-5">
                {otherParticipantsAnswers &&
                  otherParticipantsAnswers.filter(
                    (answer) => answer.integerAnswer === value,
                  ).length > 0 && (
                    <Avatars
                      showNullAsAnonymous
                      users={otherParticipantsAnswers
                        .filter((answer) => answer.integerAnswer === value)
                        .map((answer) => answer.responder)}
                      size={5}
                      max={3}
                      modalTitle="Responders"
                    />
                  )}
              </div>
            )}
            {answerHasChanged && value === answer && (
              <Tooltip text="This answer has been modified">
                <div className="font-semibold text-amber-700">*</div>
              </Tooltip>
            )}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

export default AssessmentQuestionRangeOptions;
